import React, { useEffect, useMemo, useRef, useState } from 'react';
import Modal from 'react-modal';
import { resolveMediaUrl } from '../utils/api';
import { formatSecondsToTimecode, parseTimecodeToSeconds } from '../utils/timecode';
import { niceNameFromUrl, normalizeAttachmentKey } from '../utils/attachments';
import useVideoThumbnail from '../hooks/useVideoThumbnail';
import { defaultAttachmentState } from '../hooks/useAttachmentControls';

Modal.setAppElement('#root');

const mediaIcons = {
    audio: '🎵',
    file: '📎',
    video: '🎬',
};

const getAttachmentKey = (attachment) => (
    attachment?.urlKey || normalizeAttachmentKey(attachment?.url || '') || attachment?.id
);

const AttachmentRegiePanel = ({ questionId, attachments = [], getAttachmentState, attachmentActions, onAttachmentSelect }) => {
    const [selectedAttachmentKey, setSelectedAttachmentKey] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [timeInputs, setTimeInputs] = useState({});
    const [timeErrors, setTimeErrors] = useState({});
    const { getThumb } = useVideoThumbnail();
    const previewMediaRef = useRef(null);

    useEffect(() => {
        if (!attachments.length) {
            setSelectedAttachmentKey(null);
            onAttachmentSelect && onAttachmentSelect(null);
            return;
        }
        const [first] = attachments;
        setSelectedAttachmentKey((prev) => {
            const stillValid = prev && attachments.some((a) => getAttachmentKey(a) === prev);
            if (stillValid) return prev;
            const urlKey = getAttachmentKey(first);
            onAttachmentSelect && onAttachmentSelect(urlKey);
            return urlKey;
        });
    }, [attachments, onAttachmentSelect]);

    const enrichedAttachments = useMemo(() => {
        return attachments.map((att) => ({
            ...att,
            displayName: att.title || niceNameFromUrl(att.url),
            ctrl: getAttachmentState(questionId, att),
        }));
    }, [attachments, getAttachmentState, questionId]);

    const selectedAttachment = useMemo(
        () => enrichedAttachments.find((att) => getAttachmentKey(att) === selectedAttachmentKey) || null,
        [enrichedAttachments, selectedAttachmentKey]
    );

    useEffect(() => {
        enrichedAttachments.forEach((item) => {
            const ctrl = item.ctrl || defaultAttachmentState;
            const formatted = formatSecondsToTimecode(ctrl.startAtSec ?? 0);
            setTimeInputs((prev) => {
                if (ctrl.state === 'playing') return prev;
                if (prev[item.id] === formatted) return prev;
                return { ...prev, [item.id]: formatted };
            });
            setTimeErrors((prev) => ({ ...prev, [item.id]: '' }));
        });
    }, [enrichedAttachments]);

    const handleSelect = (attachment) => {
        const urlKey = getAttachmentKey(attachment);
        setSelectedAttachmentKey(urlKey);
        onAttachmentSelect && onAttachmentSelect(urlKey);
    };

    const handlePositionChange = (attachment, nextPosition) => {
        const urlKey = normalizeAttachmentKey(attachment.url);
        attachmentActions.setControl({
            questionId,
            attachmentId: attachment.id,
            url: urlKey,
            urlKey,
            position: nextPosition,
        });
    };

    const applyStartTime = (attachment) => {
        const ctrl = attachment.ctrl || defaultAttachmentState;
        const rawValue = timeInputs[attachment.id] ?? '';
        const parsed = parseTimecodeToSeconds(rawValue);
        if (parsed === null) {
            setTimeErrors((prev) => ({ ...prev, [attachment.id]: 'Bitte MM:SS (Sekunden 00–59)' }));
            return null;
        }
        const clamped = Number.isFinite(ctrl.duration) && ctrl.duration > 0
            ? Math.min(parsed, Math.floor(ctrl.duration))
            : parsed;
        setTimeErrors((prev) => ({ ...prev, [attachment.id]: '' }));
        setTimeInputs((prev) => ({ ...prev, [attachment.id]: formatSecondsToTimecode(clamped) }));
        const urlKey = normalizeAttachmentKey(attachment.url);
        attachmentActions.setControl({
            questionId,
            attachmentId: attachment.id,
            url: urlKey,
            urlKey,
            startAtSec: clamped,
            currentTime: clamped,
        });
        return clamped;
    };

    const handleToggleVisibility = (attachment) => {
        const ctrl = attachment.ctrl || defaultAttachmentState;
        const nextVisible = !ctrl.visible;
        const startAtSec = (attachment.kind === 'video' || attachment.kind === 'audio')
            ? applyStartTime(attachment)
            : ctrl.startAtSec;
        if ((attachment.kind === 'video' || attachment.kind === 'audio') && startAtSec === null) return;
        const urlKey = normalizeAttachmentKey(attachment.url);
        attachmentActions.setControl({
            questionId,
            attachmentId: attachment.id,
            url: urlKey,
            urlKey,
            visible: nextVisible,
            position: ctrl.position || 'above',
            state: ctrl.state === 'playing' ? 'paused' : ctrl.state || 'stopped',
            startAtSec: startAtSec ?? 0,
            currentTime: ctrl.currentTime ?? startAtSec ?? 0,
        });
    };

    const handlePlay = (attachment) => {
        const ctrl = attachment.ctrl || defaultAttachmentState;
        const atSeconds = ctrl.state === 'stopped'
            ? ctrl.startAtSec ?? 0
            : ctrl.currentTime ?? ctrl.startAtSec ?? 0;
        attachmentActions.play(questionId, attachment, { atSeconds });
    };

    const syncPreviewTime = async (attachment) => {
        const mediaEl = previewMediaRef.current;
        if (!mediaEl) return;
        const { currentTime, duration } = mediaEl;
        if (!Number.isFinite(currentTime) || !Number.isFinite(duration)) return;
        await attachmentActions.timeSync(questionId, attachment, {
            currentTimeSec: currentTime,
            durationSec: duration,
        });
    };

    const handlePause = async (attachment) => {
        await syncPreviewTime(attachment);
        attachmentActions.pause(questionId, attachment);
    };

    const handleStop = (attachment) => {
        const ctrl = attachment.ctrl || defaultAttachmentState;
        attachmentActions.stop(questionId, attachment, { startAtSec: ctrl.startAtSec ?? 0 });
    };

    const handleSeek = async (attachment) => {
        const ctrl = attachment.ctrl || defaultAttachmentState;
        if (ctrl.state === 'playing') return;
        await syncPreviewTime(attachment);
        const parsed = applyStartTime(attachment);
        if (parsed === null) return;
        attachmentActions.seek(questionId, attachment, { atSeconds: parsed, currentTime: parsed });
    };

    const renderThumb = (attachment) => {
        const resolved = resolveMediaUrl(attachment.url);
        if (attachment.kind === 'image') {
            return <img src={resolved} alt="" className="filmstrip-thumb" loading="lazy" />;
        }
        if (attachment.kind === 'video') {
            const thumb = getThumb(resolved);
            if (thumb?.type === 'image') {
                return <img src={thumb.src} alt="" className="filmstrip-thumb" loading="lazy" />;
            }
            return (
                <video
                    className="filmstrip-thumb"
                    src={resolved}
                    muted
                    preload="metadata"
                    playsInline
                />
            );
        }
        if (attachment.kind === 'audio') {
            return <div className="filmstrip-thumb placeholder">{mediaIcons.audio}</div>;
        }
        return <div className="filmstrip-thumb placeholder">{mediaIcons.file}</div>;
    };

    const renderPreviewBody = (attachment) => {
        const resolved = resolveMediaUrl(attachment.url);
        if (attachment.kind === 'image') {
            return <img src={resolved} alt={attachment.displayName || 'Attachment'} className="preview-media" />;
        }
        if (attachment.kind === 'video') {
            return <video ref={previewMediaRef} src={resolved} className="preview-media" controls muted preload="metadata" />;
        }
        if (attachment.kind === 'audio') {
            return <audio ref={previewMediaRef} src={resolved} className="preview-media" controls preload="metadata" />;
        }
        return (
            <a href={resolved} target="_blank" rel="noreferrer" className="preview-link">
                {attachment.displayName || resolved}
            </a>
        );
    };

    return (
        <div className="card attachments-compact regie-card">
            <div className="attachment-list-header">
                <h4 className="card-title">Attachments Regie</h4>
                <span className="attachment-counter">{attachments.length}</span>
            </div>
            {attachments.length === 0 && <p className="text-muted">Keine Attachments</p>}
            {attachments.length > 0 && (
                <div className="regie-panel">
                    <div className="filmstrip" role="list">
                        {enrichedAttachments.map((attachment) => {
                            const ctrl = attachment.ctrl || defaultAttachmentState;
                            const isSelected = selectedAttachment?.id === attachment.id;
                            return (
                                <button
                                    key={attachment.urlKey || attachment.id}
                                    type="button"
                                    className={`filmstrip-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => handleSelect(attachment)}
                                    title={attachment.displayName}
                                >
                                    <div className="visible-indicator" data-visible={ctrl.visible} />
                                    {renderThumb(attachment)}
                                    <div className="filmstrip-label">{attachment.displayName}</div>
                                </button>
                            );
                        })}
                    </div>
                    {selectedAttachment && (
                        <div className="detail-panel">
                            <div className="detail-header">
                                <div>
                                    <div className="detail-title">{selectedAttachment.displayName}</div>
                                    <div className="detail-subtitle">{selectedAttachment.kind}</div>
                                </div>
                                <div className="detail-actions">
                                    <button type="button" className="btn btn-sm btn-surface" onClick={() => setPreviewAttachment(selectedAttachment)}>
                                        Preview
                                    </button>
                                    <button
                                        type="button"
                                        className={`btn btn-sm ${(selectedAttachment.ctrl || defaultAttachmentState).visible ? 'btn-secondary' : 'btn-primary'}`}
                                        onClick={() => handleToggleVisibility(selectedAttachment)}
                                    >
                                        {(selectedAttachment.ctrl || defaultAttachmentState).visible ? 'Hide' : 'Show'}
                                    </button>
                                </div>
                            </div>

                            <div className="input-group compact">
                                <label>Position</label>
                                <select
                                    value={(selectedAttachment.ctrl || defaultAttachmentState).position || 'above'}
                                    onChange={(e) => handlePositionChange(selectedAttachment, e.target.value)}
                                >
                                    <option value="overlay">Overlay (Fullscreen)</option>
                                    <option value="right">Rechts neben Frage</option>
                                    <option value="above">Über Frage</option>
                                </select>
                            </div>

                            {(selectedAttachment.kind === 'video' || selectedAttachment.kind === 'audio') && (
                                <>
                                    <div className="input-group compact">
                                        <label>Startzeit (MM:SS)</label>
                                        <input
                                            type="text"
                                            value={timeInputs[selectedAttachment.id] ?? formatSecondsToTimecode((selectedAttachment.ctrl || defaultAttachmentState).startAtSec ?? 0)}
                                            disabled={(selectedAttachment.ctrl || defaultAttachmentState).state === 'playing'}
                                            onChange={(e) => setTimeInputs((prev) => ({ ...prev, [selectedAttachment.id]: e.target.value }))}
                                            onBlur={() => handleSeek(selectedAttachment)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleSeek(selectedAttachment);
                                                }
                                            }}
                                            placeholder="MM:SS"
                                        />
                                        {timeErrors[selectedAttachment.id] && <small className="text-muted">{timeErrors[selectedAttachment.id]}</small>}
                                        {(selectedAttachment.ctrl || defaultAttachmentState).state === 'playing' && <small className="text-muted">Zeit ändern nur bei Pause/Stopp</small>}
                                    </div>

                                    <div className="button-row wrap">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-surface"
                                            onClick={() => handlePlay(selectedAttachment)}
                                        >
                                            Play
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-surface"
                                            onClick={() => handlePause(selectedAttachment)}
                                        >
                                            Pause
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-surface"
                                            onClick={() => handleStop(selectedAttachment)}
                                        >
                                            Stop
                                        </button>
                                    </div>

                                    <div className="media-progress">
                                        <div className="media-progress-track">
                                            <div
                                                className="media-progress-bar"
                                                style={{
                                                    width: `${(selectedAttachment.ctrl || defaultAttachmentState).duration
                                                        ? Math.min(((selectedAttachment.ctrl || defaultAttachmentState).currentTime || 0) / (selectedAttachment.ctrl || defaultAttachmentState).duration, 1) * 100
                                                        : 0}%`,
                                                }}
                                            />
                                        </div>
                                        <div className="media-progress-label">
                                            {formatSecondsToTimecode((selectedAttachment.ctrl || defaultAttachmentState).currentTime || 0)}
                                            {(selectedAttachment.ctrl || defaultAttachmentState).duration ? ` / ${formatSecondsToTimecode((selectedAttachment.ctrl || defaultAttachmentState).duration)}` : ''}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}

            <Modal
                isOpen={!!previewAttachment}
                onRequestClose={() => {
                    setPreviewAttachment(null);
                    previewMediaRef.current = null;
                }}
                className="modal-content"
                overlayClassName="modal-overlay"
            >
                {previewAttachment && (
                    <div className="attachment-preview">
                        <h4>{previewAttachment.displayName || previewAttachment.title || 'Preview'}</h4>
                        <div className="preview-body">{renderPreviewBody(previewAttachment)}</div>
                        <div className="button-row" style={{ marginTop: '12px' }}>
                            <button
                                className="btn btn-primary"
                                onClick={() => window.open(resolveMediaUrl(previewAttachment.url), '_blank', 'noopener')}
                            >
                                In neuem Tab öffnen
                            </button>
                            <button className="btn btn-ghost" onClick={() => setPreviewAttachment(null)}>
                                Schließen
                            </button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default AttachmentRegiePanel;
